import { memo, useEffect, useRef, useState } from "react";
import {
  DesktopPermissionDemo,
  DesktopWorkflowDemo,
} from "@/components/DesktopSetupPreview";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  CursorIcon,
  SystemIcon,
  ChevronLeftIcon,
  CloseIcon,
  MicrophoneIcon,
} from "@/components/Icons";
import { type DesktopPermissionStatus } from "@/clients/desktop.client";
import { desktopTriggerLabel } from "@/desktop-trigger-label";
import {
  getDesktopSetupOpeningStep,
  getDesktopSetupPermissions,
  type DesktopDictationStep,
} from "@/dictation-interactions";
import { useSharedDesktopHost } from "@/models/desktop-host-context";
import { useAppStore } from "@/controller";
import {
  ALL_PERSONAS,
  getDesktopWorkflowOrder,
} from "@/models/onboardingPersona";
import { logClientEvent } from "@/telemetry";

type Props = {
  open: boolean;
  initialStep?: DesktopDictationStep;
  onClose: () => void;
  onComplete: () => void;
};

type DesktopPlatform = "macos" | "windows";
type PermissionKind = "microphone" | "accessibility" | "systemAudio";
const PermissionStep = ({
  platform,
  permissions,
  grantedPermissions,
  busy,
  selected,
  onSelect,
  onRequest,
}: {
  platform: DesktopPlatform;
  permissions?: DesktopPermissionStatus;
  grantedPermissions: readonly PermissionKind[];
  busy: boolean;
  selected: PermissionKind | undefined;
  onSelect: (kind: PermissionKind) => void;
  onRequest: (kind: PermissionKind) => void;
}) => {
  const { t } = useTranslation();
  const kinds = getDesktopSetupPermissions(platform, permissions?.systemAudio);
  const granted = (kind: PermissionKind) => grantedPermissions.includes(kind);
  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight sm:text-[28px]">
        {t("desktopSetup.permissionsTitle")}
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-[var(--text-secondary)]">
        {t("desktopSetup.permissionsBody")}
      </p>
      <div className="mt-5 grid items-start gap-6 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <ol className="divide-y divide-[var(--fill-2)]">
          {kinds.map((kind) => (
            <li
              key={kind}
              data-testid={`desktop-permission-${kind}`}
              data-granted={granted(kind)}
            >
              <button
                type="button"
                data-action-id="dictation.setup.permission.select"
                disabled={busy || !permissions || granted(kind)}
                onClick={() => onSelect(kind)}
                aria-expanded={selected === kind}
                aria-controls="desktop-permission-guide"
                aria-labelledby={`desktop-permission-title-${kind}`}
                className={`flex w-full items-center gap-3 rounded-xl px-2 py-4 text-start focus-visible:outline-2 focus-visible:outline-[var(--accent)] disabled:cursor-default ${granted(kind) ? "opacity-60" : selected === kind ? "bg-[var(--fill-1)]" : "hover:bg-[var(--fill-1)]"}`}
              >
                <span
                  aria-hidden="true"
                  data-testid={`desktop-permission-icon-${kind}`}
                  className={`flex size-8 shrink-0 items-center justify-center rounded-full ${granted(kind) ? "bg-[var(--accent)] text-[var(--text-inverse)]" : "bg-[var(--fill-2)]"}`}
                >
                  {granted(kind) ? (
                    <CheckIcon size={16} />
                  ) : kind === "microphone" ? (
                    <MicrophoneIcon size={16} />
                  ) : kind === "systemAudio" ? (
                    <SystemIcon size={16} />
                  ) : (
                    <CursorIcon size={16} />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    id={`desktop-permission-title-${kind}`}
                    className="block text-sm font-semibold"
                  >
                    {t(
                      kind === "systemAudio"
                        ? "desktopSetup.story.computerAudio"
                        : `desktopDictation.${kind}`,
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-[var(--text-secondary)]">
                    {t(`desktopSetup.story.${kind}Reason`)}
                  </span>
                  {granted(kind) ? (
                    <span
                      role="status"
                      className="mt-1 block text-xs text-[var(--text-secondary)]"
                    >
                      {t("desktopSetup.story.allowed")}
                    </span>
                  ) : null}
                </span>
                {!granted(kind) ? (
                  <ChevronLeftIcon
                    size={18}
                    className="shrink-0 rotate-180 rtl:rotate-0"
                  />
                ) : null}
              </button>
            </li>
          ))}
        </ol>
        {selected ? (
          <div
            key={selected}
            id="desktop-permission-guide"
            role="region"
            aria-labelledby={`desktop-permission-title-${selected}`}
            data-permission={selected}
            className="desktop-permission-detail min-w-0 rounded-2xl bg-[var(--fill-1)] p-4 md:max-h-[calc(100dvh-300px)] md:overflow-y-auto"
          >
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              {t(
                selected === "systemAudio"
                  ? "desktopSetup.story.audioBody"
                  : `desktopDictation.onboarding.${selected}.body`,
              )}
            </p>
            <button
              type="button"
              data-action-id="dictation.setup.permission.request"
              disabled={busy || !permissions}
              onClick={() => onRequest(selected)}
              className="my-4 min-h-11 rounded-full bg-[var(--accent)] px-5 text-sm font-semibold text-[var(--text-inverse)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] disabled:opacity-40"
            >
              {t(
                selected === "systemAudio"
                  ? "desktopSetup.checkAudio"
                  : selected === "microphone" &&
                      permissions?.microphone === "not-determined"
                    ? "desktopDictation.onboarding.cta.allowMicrophone"
                    : "globalDictationOnboarding.cta.openSettings",
              )}
            </button>
            <DesktopPermissionDemo
              key={`${selected}-${platform}`}
              kind={selected}
              platform={platform}
              prompt={
                selected === "microphone" &&
                platform === "macos" &&
                permissions?.microphone === "not-determined"
              }
            />
            <p className="mt-3 text-xs leading-relaxed text-[var(--text-secondary)]">
              {t(
                selected === "systemAudio"
                  ? platform === "macos"
                    ? "desktopSetup.systemAudioPending"
                    : "desktopSetup.guide.windowsAudioDetail3"
                  : "desktopSetup.guide.return",
              )}
            </p>
          </div>
        ) : null}
      </div>
      {permissions?.systemAudio === "unsupported" ? (
        <p className="mt-4 text-sm text-[var(--text-secondary)]">
          {t("desktopSetup.systemAudioUnsupported")}
        </p>
      ) : null}
    </div>
  );
};

const GlobalDictationOnboardingDesktop = ({
  open,
  initialStep,
  onClose,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (open && dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, [open]);
  const host = useSharedDesktopHost();
  const persona = useAppStore((state) => state.onboardingPersona);
  const workflowOrder = getDesktopWorkflowOrder(persona);
  const [stepKey, setStepKey] = useState(() =>
    getDesktopSetupOpeningStep(initialStep),
  );
  const [workflow, setWorkflow] = useState<"upload" | "record" | "dictate">(
    () => workflowOrder[0],
  );
  const [demoReplay, setDemoReplay] = useState(0);
  const [activePermission, setActivePermission] = useState<PermissionKind>();
  const [systemAudioChecked, setSystemAudioChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    let resumeAudio = false;
    try {
      resumeAudio = localStorage.getItem("desktopSetup.v1") === "systemAudio";
    } catch {
      /* Education remains usable without persistence. */
    }
    setStepKey(getDesktopSetupOpeningStep(initialStep, resumeAudio));
    setWorkflow(
      getDesktopWorkflowOrder(useAppStore.getState().onboardingPersona)[0],
    );
    setActivePermission(
      resumeAudio || initialStep === "systemAudio"
        ? "systemAudio"
        : initialStep === "accessibility"
          ? "accessibility"
          : "microphone",
    );
    setSystemAudioChecked(false);
  }, [open, initialStep]);

  useEffect(() => {
    if (!open) return;
    logClientEvent("dictation.onboardingStepViewed", {
      step: stepKey,
      platform: "desktop",
    });
  }, [open, stepKey]);

  if (!open) return null;

  const platform = host.info?.platform ?? "macos";
  const kinds = getDesktopSetupPermissions(
    platform,
    host.permissions?.systemAudio,
  );
  const permissionGranted = (permission: PermissionKind) =>
    permission === "systemAudio"
      ? systemAudioChecked && host.permissions?.systemAudio === "granted"
      : host.permissions?.[permission] === "granted";
  const grantedPermissions = kinds.filter(permissionGranted);
  const selectedPermission =
    kinds.find(
      (kind) => kind === activePermission && !permissionGranted(kind),
    ) ?? kinds.find((kind) => !permissionGranted(kind));
  const permissionsReady = Boolean(host.permissions) && !selectedPermission;
  const shortcutLabel = host.settings
    ? desktopTriggerLabel(host.settings.triggerKey, platform, t)
    : t("desktopDictation.shortcutFallback");
  const requestPermission = async (permission: PermissionKind) => {
    if (host.busy || permissionGranted(permission)) return;
    if (permission === "systemAudio" && platform === "macos") {
      // macOS may ask for a relaunch. Resume the checklist, never infer a grant.
      try {
        localStorage.setItem("desktopSetup.v1", "systemAudio");
      } catch {
        /* Setup is still usable without persistence. */
      }
    }
    const next = await host.requestPermission(permission);
    if (permission === "systemAudio" && next?.systemAudio === "granted") {
      setSystemAudioChecked(true);
      try {
        localStorage.removeItem("desktopSetup.v1");
      } catch {
        /* Non-essential education checkpoint. */
      }
    }
  };
  const showDemos = () => {
    setWorkflow(
      getDesktopWorkflowOrder(useAppStore.getState().onboardingPersona)[0],
    );
    setDemoReplay((value) => value + 1);
    setStepKey("intro");
  };
  const finish = () => {
    if (stepKey === "persona") {
      showDemos();
      return;
    }
    if (stepKey === "intro") {
      setStepKey("permissions");
      return;
    }
    logClientEvent("dictation.onboardingCompleted", {
      platform: "desktop",
      granted: permissionsReady,
    });
    onComplete();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-label={t("desktopSetup.title")}
      data-testid="desktop-setup"
      data-step={stepKey}
      data-workflow={workflow}
      onCancel={(event) => event.preventDefault()}
      className="app-page-background fixed inset-0 z-[900] m-0 flex h-dvh max-h-none w-screen max-w-none flex-col border-0 bg-[var(--bg-primary)] p-0 text-[var(--text-primary)]"
    >
      <div className="relative mx-auto flex h-full w-full max-w-5xl flex-col px-5 pb-10 pt-4 [@media(max-height:700px)]:pb-6">
        <header className="flex items-center gap-3">
          <button
            type="button"
            data-action-id="dictation.setup.backOrClose"
            disabled={host.busy}
            onClick={
              stepKey === "permissions"
                ? () => setStepKey("intro")
                : stepKey === "intro"
                  ? () => setStepKey("persona")
                  : onClose
            }
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full glass-tile disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            aria-label={t(
              stepKey === "permissions" || stepKey === "intro"
                ? "globalDictationOnboarding.back"
                : "globalDictationOnboarding.close",
            )}
          >
            {stepKey === "permissions" || stepKey === "intro" ? (
              <ChevronLeftIcon size={20} className="rtl:rotate-180" />
            ) : (
              <CloseIcon size={18} />
            )}
          </button>
          <ol
            className="mx-auto flex min-w-0 flex-1 justify-center gap-3 sm:gap-6"
            aria-label={t("desktopSetup.step", {
              count: ["persona", "intro", "permissions"].indexOf(stepKey) + 1,
            })}
          >
            {(["persona", "intro", "permissions"] as const).map(
              (step, index) => (
                <li
                  key={step}
                  aria-current={stepKey === step ? "step" : undefined}
                  className={`flex flex-col items-center gap-1 text-center text-[11px] sm:flex-row sm:gap-1.5 sm:text-sm ${stepKey === step ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-tertiary)]"}`}
                >
                  <span
                    className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs ${stepKey === step ? "bg-[var(--accent)] text-[var(--text-inverse)]" : "bg-[var(--fill-2)]"}`}
                  >
                    {index + 1}
                  </span>
                  {t(`desktopSetup.steps.${step}`)}
                </li>
              ),
            )}
          </ol>
          <span className="w-11" />
        </header>
        <main
          key={stepKey}
          className="min-h-0 flex-1 overflow-y-auto px-2 pb-4 pt-6"
        >
          {stepKey === "persona" ? (
            <div className="mx-auto max-w-2xl pt-2 sm:pt-6">
              <h1 className="max-w-xl text-3xl font-semibold tracking-tight sm:text-4xl">
                {t("desktopSetup.personaTitle")}
              </h1>
              <p className="mt-3 text-sm text-[var(--text-secondary)]">
                {t("desktopSetup.personaBody")}
              </p>
              <div
                className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3"
                role="group"
                aria-label={t("onboarding.persona.headline")}
              >
                {ALL_PERSONAS.filter((option) => option !== "hearing-loss").map(
                  (option) => (
                    <button
                      key={option}
                      type="button"
                      data-action-id="dictation.setup.persona.select"
                      data-persona={option}
                      aria-pressed={persona === option}
                      onClick={() =>
                        useAppStore.getState().setOnboardingPersona(option)
                      }
                      className={`flex min-h-16 items-center justify-between gap-2 rounded-2xl px-4 py-3 text-start text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${persona === option ? "selected-surface" : "glass-tile"}`}
                    >
                      {t(
                        option === "other"
                          ? "desktopSetup.personaOther"
                          : `onboarding.persona.label_${option}`,
                      )}
                      {persona === option ? (
                        <CheckIcon
                          size={16}
                          className="shrink-0 text-[var(--accent)]"
                        />
                      ) : null}
                    </button>
                  ),
                )}
              </div>
            </div>
          ) : stepKey === "intro" ? (
            <div className="mx-auto grid max-w-5xl items-start gap-5 md:grid-cols-[230px_minmax(0,1fr)]">
              <div>
                <h1
                  className="mb-5 text-2xl font-semibold tracking-tight"
                  data-testid="desktop-persona-benefit"
                >
                  {t(`desktopSetup.day.${persona ?? "other"}`)}
                </h1>
                <div
                  className="grid grid-cols-3 gap-2 md:grid-cols-1"
                  role="group"
                  aria-label={t("desktopSetup.steps.intro")}
                >
                  {workflowOrder.map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      data-action-id="dictation.setup.demo.select"
                      data-demo={kind}
                      onClick={() => {
                        setWorkflow(kind);
                        setDemoReplay((value) => value + 1);
                      }}
                      aria-pressed={workflow === kind}
                      className={`min-h-16 rounded-xl px-3 py-3 text-start focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${workflow === kind ? "bg-[var(--accent)] text-[var(--text-inverse)]" : "bg-[var(--fill-1)] text-[var(--text-secondary)]"}`}
                    >
                      <span className="block text-sm font-semibold">
                        {t([
                          `desktopSetup.benefit.${kind}_${persona}`,
                          `desktopSetup.benefit.${kind}`,
                        ])}
                      </span>
                      <span className="mt-1 block text-xs opacity-80">
                        {t(
                          kind === "dictate"
                            ? "webDictation.dictate"
                            : kind === "record"
                              ? "home.record"
                              : "desktopSetup.upload",
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <DesktopWorkflowDemo
                key={`${persona}-${workflow}-${demoReplay}`}
                kind={workflow}
                persona={persona}
                shortcut={shortcutLabel}
                platform={platform}
                onComplete={() =>
                  setWorkflow(
                    workflowOrder[
                      (workflowOrder.indexOf(workflow) + 1) %
                        workflowOrder.length
                    ],
                  )
                }
              />
            </div>
          ) : (
            <PermissionStep
              platform={platform}
              permissions={host.permissions}
              grantedPermissions={grantedPermissions}
              busy={host.busy}
              selected={selectedPermission}
              onSelect={setActivePermission}
              onRequest={(kind) => void requestPermission(kind)}
            />
          )}
        </main>
        <footer className="mx-auto w-full max-w-3xl shrink-0 px-1 pt-2">
          {stepKey === "permissions" && host.operationFailed ? (
            <p
              role="status"
              className="mb-3 text-center text-sm text-[var(--destructive)]"
            >
              {t("desktopSetup.guide.failed")}
            </p>
          ) : null}
          <button
            type="button"
            data-action-id="dictation.setup.continue"
            disabled={
              host.busy ||
              (stepKey === "persona" &&
                (!persona || persona === "hearing-loss")) ||
              (stepKey === "permissions" && !host.permissions)
            }
            onClick={
              stepKey === "permissions" && selectedPermission
                ? () => void requestPermission(selectedPermission)
                : finish
            }
            className="h-14 w-full rounded-full bg-[var(--accent)] text-base font-semibold text-[var(--text-inverse)] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            {t(
              stepKey === "persona"
                ? "desktopSetup.personaContinue"
                : stepKey === "intro"
                  ? "desktopSetup.continueSetup"
                  : selectedPermission
                    ? `desktopSetup.setup.${selectedPermission}`
                    : "desktopSetup.finishSetup",
            )}
          </button>
          {stepKey === "persona" ? (
            <button
              type="button"
              data-action-id="dictation.setup.persona.skip"
              onClick={() => {
                useAppStore.getState().setOnboardingPersona(null);
                showDemos();
              }}
              className="mt-2 min-h-11 w-full rounded-full text-sm text-[var(--text-secondary)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            >
              {t("desktopSetup.personaSkip")}
            </button>
          ) : null}
          {stepKey === "permissions" && !permissionsReady ? (
            <button
              type="button"
              data-action-id="dictation.setup.skip"
              disabled={host.busy}
              onClick={finish}
              className="mt-2 min-h-11 w-full rounded-full text-sm text-[var(--text-secondary)] focus-visible:outline-2 focus-visible:outline-[var(--accent)] disabled:opacity-40"
            >
              {t("desktopSetup.skip")}
            </button>
          ) : null}
        </footer>
      </div>
    </dialog>
  );
};

export default memo(GlobalDictationOnboardingDesktop);
